import { arrayOf, bool, shape, string, number } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import { SPONSORED_SLUG } from 'constants/tags';
import { HAS_RECIPE_BOX } from 'constants/site';
import { getLinkProps } from 'util/link';
import { hasTermWithSlug } from 'util/terms';
import FormattedDate from 'components/FormattedDate';
import Link from 'components/Link';
import { MQ_LARGE } from 'constants/mq';
import IMAGE_SIZES from 'constants/imageSizes';
import Picture from 'components/Picture';
import TeaserIcons from './icons';
import TeaserImage from '../TeaserImage';
import { IS_VIDEO_ATTR } from '../../constants/teasers';

const SaveToRecipeBoxActions = dynamic(
  () => import('components/SaveToRecipeBoxActions'),
  {
    ssr: false
  }
);

// prettier-ignore
export const PLACEHOLDER_SRC = 'l_placeholders:tile-light-blue,fl_tiled/https://s3.amazonaws.com/pixtruder/original_images/dd436318cb8c08bf7d17d5838e06e296e0a25e35';

const { mediumSquare, largePortrait } = IMAGE_SIZES;

export const DEFAULT_SOURCE_SETS = [
  {
    layoutWidth: { value: 100, units: 'vw' },
    imageDims: mediumSquare,
    media: MQ_LARGE
  }
];

export const DEFAULT_FALLBACK_DIMS = largePortrait;

export function Teaser(props) {
  const {
    author,
    cta,
    dek,
    hasLazyPicture,
    headline,
    kicker,
    kickerLink,
    imageUrl: baseImageUrl,
    teaserAttributes,
    sourceSets,
    placeholders,
    fallbackDims,
    promotedAt,
    renderer,
    tags,
    variant,
    gtmAttrs,
    link,
    credit,
    postId,
    postUrl,
    flips,
    recipeId
  } = props;

  const isSponsored = hasTermWithSlug(tags, SPONSORED_SLUG);

  const teaserClassnames = classnames('Teaser', {
    [`Teaser--${variant}`]: variant,
    // cake === sponsored. In case adblock is looking at classnames
    'Teaser--cake': isSponsored,
    'Teaser--action': cta,
    'Teaser--video': teaserAttributes.includes(IS_VIDEO_ATTR)
  });

  const imageUrl = baseImageUrl || PLACEHOLDER_SRC;

  const teaserImageProps = {
    renderer,
    sourceSets,
    fallbackDims,
    placeholders,
    url: imageUrl,
    hasLazyPicture,
    credit
  };

  const isTextLink =
    variant === 'textLink' ||
    variant === 'simpleList' ||
    variant === 'navTextLink';

  return (
    <div className={teaserClassnames}>
      {!isTextLink && (
        <div className="Teaser__picture">
          {imageUrl && <TeaserImage {...teaserImageProps} />}
          {teaserAttributes.length > 0 && (
            <TeaserIcons teaserAttributes={teaserAttributes} />
          )}
        </div>
      )}

      <div className="Teaser__body">
        {isSponsored && <div className="Teaser__cake-tag">Partner</div>}
        <Link {...link}>
          <a
            className="Teaser__headline Teaser__link"
            dangerouslySetInnerHTML={{
              __html: headline
            }}
            {...gtmAttrs}
          />
        </Link>

        {kicker && kickerLink && (
          <Link {...kickerLink}>
            <a
              className="Teaser__kicker Teaser__kicker-link"
              dangerouslySetInnerHTML={{
                __html: kicker
              }}
              {...gtmAttrs}
            />
          </Link>
        )}
        {kicker && kickerLink === undefined && (
          <div
            className="Teaser__kicker"
            dangerouslySetInnerHTML={{
              __html: kicker
            }}
          />
        )}

        {dek && (
          <div
            className="Teaser__dek"
            dangerouslySetInnerHTML={{
              __html: dek
            }}
          />
        )}

        {author && author.name && (
          <div
            className={`Teaser__byline${
              author.authorUrl ? ' Teaser__byline-link' : ''
            }`}
          >
            {/*
              Hide author image until we're ready to use it. We should lazy load
              this image when we re-enable.
            */}
            {/* author.imageUrl && (
              <div className="Teaser__author-headshot">
                <Image url={author.imageUrl} />
              </div>
            ) */}
            {author.authorUrl && (
              <Link {...getLinkProps(author.authorUrl)}>
                <a
                  className="Teaser__author"
                  dangerouslySetInnerHTML={{
                    __html: author.name
                  }}
                  {...gtmAttrs}
                />
              </Link>
            )}
            {author.authorUrl === undefined && (
              <span dangerouslySetInnerHTML={{ __html: author.name }} />
            )}
          </div>
        )}

        {promotedAt && (
          <div className="Teaser__timestamp">
            <FormattedDate timestamp={promotedAt} />
          </div>
        )}

        {cta && (
          <div className="Teaser__cta">
            <span dangerouslySetInnerHTML={{ __html: cta }} />
          </div>
        )}
      </div>
      {flips.includes('flip_show_save_recipe') &&
        HAS_RECIPE_BOX &&
        recipeId !== undefined &&
        postId !== undefined && (
          <SaveToRecipeBoxActions
            recipeId={recipeId}
            postId={postId}
            postUrl={postUrl}
            gtmAttrs={{ placement: 'Round-Up Recipe Teaser' }}
          />
        )}
    </div>
  );
}

Teaser.defaultProps = {
  author: undefined,
  cta: undefined,
  dek: '',
  hasLazyPicture: true,
  kicker: '',
  kickerLink: undefined,
  imageUrl: '', // Should we choose a default image here?
  sourceSets: DEFAULT_SOURCE_SETS,
  fallbackDims: DEFAULT_FALLBACK_DIMS,
  placeholders: [],
  promotedAt: undefined,
  renderer: 'html',
  tags: [],
  categories: [],
  teaserAttributes: [],
  variant: '',
  gtmAttrs: undefined,
  link: undefined,
  credit: undefined,
  recipeId: undefined,
  postId: undefined,
  postUrl: undefined,
  flips: []
};

Teaser.propTypes = {
  headline: string.isRequired,
  author: shape({
    imageUrl: string,
    name: string
  }),
  cta: string,
  dek: string,
  hasLazyPicture: bool,
  imageUrl: string,
  kicker: string,
  kickerLink: shape({}),
  sourceSets: Picture.propTypes.sourceSets,
  fallbackDims: Picture.propTypes.fallbackDims,
  placeholders: arrayOf(
    shape({
      media: string,
      ratio: number
    })
  ),
  promotedAt: number,
  renderer: string,
  tags: arrayOf(
    shape({
      name: string,
      slug: string
    })
  ),
  categories: arrayOf(
    shape({
      name: string,
      slug: string
    })
  ),
  teaserAttributes: TeaserIcons.propTypes.teaserAttributes,
  variant: string,
  gtmAttrs: shape({}),
  link: shape({}),
  credit: string,
  recipeId: string,
  postId: string,
  postUrl: string,
  flips: arrayOf(string)
};

const mapStateToProps = state => {
  const { app } = state;
  return {
    flips: app.flips
  };
};

export default connect(mapStateToProps)(Teaser);
